'use client'

import { useCallback, useRef, useState } from 'react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { FileText, Menu } from 'lucide-react'

import type { NavigationContent } from '@/lib/content/navigation'
import { useSiteSettings } from '@/lib/site-settings-context'
import { Button } from '@/components/ui/button'
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from '@/components/ui/sheet'
import { HighlightedText } from '@/components/highlighted-text'
import { IconFromKey } from '@/components/icon-from-key'
import { NeonLogo } from '@/components/neon-logo'
import { PhoneLink } from '@/components/phone-link'
import { PunkHighlight } from '@/components/punk-highlight'
import { Sparkles } from '@/components/sparkles'
import { TopoFlow } from '@/components/topo-flow'

type HeaderProps = {
  nav: NavigationContent
  mobileMenuBlurb: string
}

function CtaLabel({ label, highlight }: { label: string; highlight: string }) {
  if (!highlight || !label.includes(highlight)) {
    return <>{label}</>
  }
  const [before, after] = label.split(highlight)
  return (
    <>
      {before}
      <PunkHighlight colour="purple" rotation="-1.5deg">
        {highlight}
      </PunkHighlight>
      {after}
    </>
  )
}

export function Header({ nav, mobileMenuBlurb }: HeaderProps) {
  const [sheetOpen, setSheetOpen] = useState(false)
  const [headerLowZ, setHeaderLowZ] = useState(false)
  const pathname = usePathname()
  const timerRef = useRef<ReturnType<typeof setTimeout>>(null)
  const { abn } = useSiteSettings()

  const handleOpenChange = useCallback((open: boolean) => {
    if (timerRef.current) clearTimeout(timerRef.current)
    setSheetOpen(open)
    if (open) {
      setHeaderLowZ(true)
    } else {
      timerRef.current = setTimeout(() => setHeaderLowZ(false), 250)
    }
  }, [])

  return (
    <header
      className={`texture-stone-dark top-0 isolate bg-[#1a0e20] ${headerLowZ ? 'z-auto' : 'z-50'}`}
      style={{ position: 'sticky', WebkitTransform: 'translate3d(0,0,0)' }}
    >
      <div className="relative z-10 mx-auto flex h-16 max-w-6xl items-center justify-between px-4 sm:h-20 sm:px-6">
        <Link href="/" className="relative flex items-center">
          <NeonLogo variant="wide" className="h-11 w-auto sm:h-14" priority />
        </Link>

        <nav className="hidden items-center gap-6 lg:flex">
          {nav.headerNavLinks
            .filter((link) => !link.hideOnDesktop)
            .map((link) => {
              const isActive = pathname === link.href
              return (
                <Link
                  key={link.href}
                  href={link.href}
                  aria-current={isActive ? 'page' : undefined}
                  className={`relative flex items-center gap-1.5 text-base font-medium whitespace-nowrap transition-colors hover:text-white ${isActive ? 'text-white' : 'text-white/70'}`}
                >
                  <IconFromKey iconKey={link.iconKey} className="size-4" />
                  {link.label}
                </Link>
              )
            })}
          <PhoneLink className="text-brand-chartreuse-vivid hover:text-brand-chartreuse flex items-center gap-1.5 text-base font-medium transition-colors" />
          <Button asChild size="lg" className="relative h-11 px-6 text-xl">
            <Link href={nav.ctaHref} title={nav.ctaTitle}>
              <FileText className="mr-1 size-5" aria-hidden="true" />
              <CtaLabel label={nav.ctaLabel} highlight={nav.ctaHighlight} />
              <Sparkles seed={1} />
            </Link>
          </Button>
        </nav>

        <Sheet open={sheetOpen} onOpenChange={handleOpenChange}>
          <SheetTrigger
            className="-mr-2 flex size-11 items-center justify-center text-white lg:hidden"
            aria-label="Toggle menu"
          >
            <Menu className="size-6" aria-hidden="true" />
          </SheetTrigger>
          <SheetContent
            side="right"
            showCloseButton
            className="border-border !w-[85%] !gap-0 overflow-y-auto overscroll-contain bg-[#1a0e20] text-white"
          >
            <TopoFlow
              seed={42}
              lineCount={6}
              speed={0.08}
              scale={0.002}
              strokeColour="rgba(135,42,135,0.12)"
              className="pointer-events-none absolute inset-0"
            />
            <Link
              href="/"
              onClick={() => handleOpenChange(false)}
              className="relative flex flex-col items-center px-4 pt-6"
            >
              <NeonLogo
                variant="centre-stacked"
                className="max-h-[100px] w-48"
                trigger={sheetOpen}
              />
            </Link>
            <div className="divider-glow relative mx-4 mt-4" />
            <SheetTitle className="sr-only">Navigation</SheetTitle>
            <nav className="relative flex flex-col gap-5 px-6 pt-4">
              {nav.headerNavLinks.map((link) => {
                const isActive = pathname === link.href
                return (
                  <Link
                    key={link.href}
                    href={link.href}
                    onClick={() => handleOpenChange(false)}
                    aria-current={isActive ? 'page' : undefined}
                    className={`flex items-center gap-3 text-xl font-semibold transition-colors hover:text-white ${isActive ? 'text-white' : 'text-white/70'}`}
                  >
                    <IconFromKey iconKey={link.iconKey} className="size-6" />
                    {link.label}
                  </Link>
                )
              })}
              <PhoneLink className="text-brand-chartreuse-vivid flex items-center gap-2 text-xl font-semibold" />
            </nav>
            <div className="divider-glow relative mx-4 mt-4" />
            <div className="relative px-4 pt-4">
              <Button asChild className="relative w-full px-6 py-4 text-2xl">
                <Link
                  href={nav.ctaHref}
                  onClick={() => handleOpenChange(false)}
                  title={nav.ctaTitle}
                >
                  <FileText className="mr-1 size-5" aria-hidden="true" />
                  <CtaLabel label={nav.ctaLabel} highlight={nav.ctaHighlight} />
                  <Sparkles seed={2} />
                </Link>
              </Button>
            </div>
            <div className="relative mt-auto px-6 pt-8 pb-6 text-center text-sm text-white/40">
              <p style={{ whiteSpace: 'pre-line' }}>
                <HighlightedText
                  text={mobileMenuBlurb}
                  highlightColour="chartreuse"
                  highlightVariant="marker"
                  highlightRotation="-3deg"
                />
              </p>
              <p className="mt-2">ABN: {abn}</p>
              <p className="mt-1">&copy; {new Date().getFullYear()} Rock&apos;n Earthworks</p>
            </div>
          </SheetContent>
        </Sheet>
      </div>
      <div
        className={`divider-glow transition-opacity duration-200 ${headerLowZ ? 'opacity-0' : 'opacity-100'}`}
      />
    </header>
  )
}
